import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { BrandLogo as BrandLogoType, Reference } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { CMSImage } from '@swf/cms';
import CasinoNetIcon from '@swf/core/assets/casino-net.svg';
import FoxBetLogo from '@swf/core/assets/fox-bet.svg';
import FullTiltIcon from '@swf/core/assets/fulltilt.svg';
import PokerStarsIcon from '@swf/core/assets/poker-menu.svg';
import PokerDeshIcon from '@swf/core/assets/poker-net-desh.svg';
import PokerSochiIcon from '@swf/core/assets/poker-net-sochi.svg';
import PokerNetIcon from '@swf/core/assets/poker-net.svg';
import PokerStarsCasinoLogo from '@swf/core/assets/pokerstars-casino.svg';
import PokerStarsLogo from '@swf/core/assets/pokerstars-logo.svg';
import { select } from '@swf/core/store';

import useCustomHeader from '../../../../hooks/useCustomHeader';

import styles from './BrandIcon.scss';

interface Props {
  inMenu?: boolean;
  brandIcon: Reference[];
}

const sochiRegions = ['RU', 'RU2'];
const usaRegion = 'US';
const deshRegion = 'DE';

const BrandLogo: React.FC<{ site: Sites; brand: BrandTypes; className: string }> = ({
  site,
  brand,
  className
}) =>
  site === Sites.USMI && brand === BrandTypes.STARS_CASINO ? (
    <PokerStarsCasinoLogo width={130} height={28} className={className} />
  ) : (
    <PokerStarsLogo width={130} height={22} className={className} />
  );

const BrandIcon: React.FC<Props> = ({ inMenu = false, brandIcon }) => {
  const brand = useSelector(select.cms.getBrandId);
  const site = useSelector(select.cms.getSiteId);
  const region = useSelector(select.cms.getRegion);
  const baseURL = useSelector(select.location.getLocaleURL);
  const { lightHeader } = useCustomHeader();
  const isPrototype4Enabled = useFeature(FeatureFlag.PKRNavDesignV3);

  const brandIconClass = (inMenu ? '' : 'brandIcon') as keyof typeof styles;

  if (brandIcon?.length) {
    const [{ desktop, tablet, mobile }] = brandIcon as BrandLogoType[];
    if (inMenu) {
      return <CMSImage {...mobile} />;
    }

    return (
      <>
        <CMSImage {...tablet} className={classNames(styles.brandIcon, styles.tabletLogo)} />
        <CMSImage {...desktop} className={classNames(styles.brandIcon, styles.desktopLogo)} />
      </>
    );
  }

  switch (true) {
    case brand === BrandTypes.FULL_TILT:
      return (
        <a href={baseURL || '/'}>
          <FullTiltIcon className={styles[brandIconClass]} />
        </a>
      );
    case brand === BrandTypes.FOX_BET:
      return <FoxBetLogo height={22} className={styles[brandIconClass]} />;
    case site === Sites.NET && deshRegion === region:
      return <PokerDeshIcon width={234} height={37} className={styles[brandIconClass]} />;
    case site === Sites.NET && region && sochiRegions.includes(region):
      return <PokerSochiIcon width={182} height={39} className={styles[brandIconClass]} />;
    case site === Sites.NET && brand === BrandTypes.POKER_STARS && region !== usaRegion:
      return <PokerNetIcon width={182} height={39} className={styles[brandIconClass]} />;
    case site === Sites.NET && brand === BrandTypes.STARS_CASINO:
      return <CasinoNetIcon width={182} height={45} className={styles[brandIconClass]} />;
    case inMenu:
    case site === Sites.NET && brand === BrandTypes.POKER_STARS && region === usaRegion:
      return <BrandLogo site={site} brand={brand} className={styles[brandIconClass]} />;
    default:
      return lightHeader || isPrototype4Enabled ? (
        <>
          <PokerStarsIcon
            height={22}
            className={classNames(styles.brandIcon, styles.pokerstarsIcon, styles.tabletLogo)}
          />
          <BrandLogo
            site={site}
            brand={brand}
            className={classNames(styles[brandIconClass], styles.desktopLogo)}
          />
        </>
      ) : (
        <PokerStarsIcon
          width={60}
          height={28}
          className={classNames(styles.brandIcon, styles.pokerstarsIcon)}
        />
      );
  }
};

export default BrandIcon;
